import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { OperationSchedule } from '@/features/driveRoutes/type/operationSchedule';

type RoutesInfoShowProps = {
  driveList: OperationSchedule[];
};

type Waypoint = OperationSchedule['waypointList'][number];

const buildWaypointPairs = (waypoints: Waypoint[]) => {
  const handledReservationIds = new Set<number>();
  const pairs: { pickup?: Waypoint; dropoff?: Waypoint }[] = [];

  waypoints.forEach((waypoint) => {
    if (handledReservationIds.has(waypoint.reservationId)) {
      return;
    }

    const related = waypoints.filter((w) => w.reservationId === waypoint.reservationId);
    const pickup = related.find((w) => w.isPickup);
    const dropoff = related.find((w) => !w.isPickup);

    if (pickup || dropoff) {
      pairs.push({ pickup, dropoff });
    }

    related.forEach((w) => {
      handledReservationIds.add(w.reservationId);
    });
  });

  return pairs;
};

export const RoutesInfoShow: React.FC<RoutesInfoShowProps> = ({ driveList }) => {
  const { t } = useTranslation();

  return (
    <Box>
      {driveList?.map((drive) => {
        const driveKey = drive.waypointList
          .map((waypoint) => `${waypoint.reservationId}-${waypoint.waypointId}-${waypoint.isPickup ? 'p' : 'd'}`)
          .join('_');

        return (
          <Box key={driveKey}>
            <Box>
              {buildWaypointPairs(drive.waypointList).map((pair) => {
                const isEntrusted = pair.pickup?.isNewReservation === false || pair.dropoff?.isNewReservation === false;
                const pairKey = `${pair.pickup?.reservationId ?? 'none'}-${pair.dropoff?.reservationId ?? 'none'}`;

                return (
                  <Box key={pairKey} className={`waypoint-confirm-card${isEntrusted ? ' is-entrusted' : ''}`}>
                    {pair.pickup && (
                      <div className="waypoint-confirm-column">
                        <div className="route-time-row">
                          <span className="route-time">
                            {dayjs(pair.pickup.estimatedArrivalDatetime).format('HH:mm')}
                          </span>
                        </div>
                        <div className="waypoint-confirm-row">
                          <span className="status-badge is-pickup">乗</span>
                          <div className="waypoint-confirm-content">
                            <div className="waypoint-tag is-pickup">{pair.pickup.waypointNumber}</div>
                            <Typography className="route-location">
                              {t('driveRoutes.pickup')} {pair.pickup.waypointName}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                    {pair.dropoff && (
                      <div className="waypoint-confirm-column">
                        <div className="route-time-row">
                          <span className="route-time">
                            {dayjs(pair.dropoff.estimatedArrivalDatetime).format('HH:mm')}
                          </span>
                        </div>
                        <div className="waypoint-confirm-row">
                          <span className="status-badge is-dropoff">降</span>
                          <div className="waypoint-confirm-content">
                            <div className="waypoint-tag is-dropoff">{pair.dropoff.waypointNumber}</div>
                            <Typography className="route-location">
                              {t('driveRoutes.dropoff')} {pair.dropoff.waypointName}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
